import { useState, useEffect } from "react";

import {
  ArrowBack as ArrowBackIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Typography,
  Alert,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
} from "@mui/material";
import {
  postBulkEditPreview,
  postBulkEditApply,
  type Selector,
  type SurveyPreview,
  type BulkEditResult,
  type ComputedDiff,
} from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router-dom";

import FormBuilder from "./FormBuilder";
import { VersionComparison } from "./VersionComparison";

/**
 * Check if a computed diff has any actual changes (added or removed lines)
 */
const hasChanges = (diff: ComputedDiff | undefined): boolean => {
  if (!diff) return false;
  return diff.left_lines.some(
    (line) => line.type === "added" || line.type === "removed",
  );
};

type VersionView = "fromVersion" | "toVersion" | "compare";

// Read bulk edit data once from sessionStorage
const getBulkEditData = () => {
  try {
    const stored = sessionStorage.getItem("bulkEditData");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const BulkEditPreview = () => {
  const navigate = useNavigate();
  const params = useParams();
  const orgId = params.orgId;

  // Store bulk edit data in state to ensure consistency across renders
  const [bulkEditData] = useState(() => getBulkEditData());

  const targetType = bulkEditData?.targetType || "";
  const targetConfig = bulkEditData?.targetConfig || "both";
  const targetContent = bulkEditData?.targetContent || "";
  const replacementContent = bulkEditData?.replacementContent || "";
  const surveyIds: string[] = bulkEditData?.surveyIds || [];
  const selector: Selector | undefined = bulkEditData?.selector;

  // State
  const [previews, setPreviews] = useState<SurveyPreview[]>([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [selectedVersionView, setSelectedVersionView] =
    useState<VersionView>("compare");
  const [isCondensedForm, setIsCondensedForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);

  const currentPreview = previews[currentPreviewIndex];

  // Load previews
  useEffect(() => {
    const loadPreviews = async () => {
      if (!orgId || surveyIds.length === 0) {
        sessionStorage.removeItem("bulkEditData");
        navigate(`/rubireportv2/${orgId}/forms/surveys`);
        return;
      }

      setIsLoading(true);

      // Validate replacement JSON on frontend for element/page types
      if (targetType === "element" || targetType === "page") {
        try {
          JSON.parse(replacementContent);
        } catch (parseError: any) {
          toast.error(`Invalid replacement JSON: ${parseError.message}`);
          sessionStorage.removeItem("bulkEditData");
          navigate(`/rubireportv2/${orgId}/forms/surveys`);
          return;
        }
      }

      try {
        const response = await postBulkEditPreview({
          path: { org_id: orgId },
          body: {
            target_type: targetType,
            target_content: targetContent,
            replacement_content: replacementContent,
            survey_ids: surveyIds,
            target_config: targetConfig,
            ...(selector ? { selector } : {}),
          },
        });

        setPreviews(response.data?.previews || []);
      } catch (error: any) {
        console.error("Failed to load previews:", error);
        // Extract error message from API response
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load bulk edit previews";
        toast.error(errorMessage);
        sessionStorage.removeItem("bulkEditData");
        navigate(`/rubireportv2/${orgId}/forms/surveys`);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreviews();
  }, [orgId, targetType, targetContent, replacementContent, surveyIds.length]);

  const handleApply = () => {
    // Apply directly - drafts will be updated, not deleted
    performApply();
  };

  const performApply = async () => {
    if (!orgId) return;

    setIsApplying(true);
    try {
      const response = await postBulkEditApply({
        path: { org_id: orgId },
        body: {
          target_type: targetType,
          target_config: targetConfig,
          target_content: targetContent,
          replacement_content: replacementContent,
          survey_ids: surveyIds,
          ...(selector ? { selector } : {}),
        },
      });

      const results = response.data?.results || [];
      const successCount = results.filter(
        (r: BulkEditResult) => r.status === "success",
      ).length;
      const failedCount = results.filter(
        (r: BulkEditResult) => r.status === "failed",
      ).length;

      if (failedCount === 0) {
        toast.success(
          `Bulk edit applied successfully to ${successCount} survey(s)`,
        );
      } else {
        toast.warning(
          `Bulk edit completed: ${successCount} succeeded, ${failedCount} failed`,
        );
      }

      // Clean up sessionStorage and navigate back to survey list
      sessionStorage.removeItem("bulkEditData");
      navigate(`/rubireportv2/${orgId}/forms/surveys`, {
        state: { bulkEditSuccess: true },
      });
    } catch (error: any) {
      console.error("Failed to apply bulk edit:", error);
      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.console_msg ||
        error?.message ||
        "Failed to apply bulk edit";
      toast.error(errorMessage);
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancel = () => {
    sessionStorage.removeItem("bulkEditData");
    navigate(`/rubireportv2/${orgId}/forms/surveys`);
  };

  const handleVersionViewChange = (event: any) => {
    setSelectedVersionView(event.target.value);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentPreview) {
    return (
      <Box p={3}>
        <Alert severity="error">No previews available</Alert>
      </Box>
    );
  }

  // Create version objects for VersionComparison and FormBuilder
  // Use source_version which could be draft or active depending on what exists
  const sourceVersion = {
    ...currentPreview.source_version,
    id: currentPreview.source_version.id || "",
    parent_survey_id: currentPreview.parent_survey_id,
    major_version: currentPreview.source_version.version_number
      ? parseInt(currentPreview.source_version.version_number.split(".")[0])
      : null,
    minor_version: currentPreview.source_version.version_number
      ? parseInt(currentPreview.source_version.version_number.split(".")[1])
      : null,
    status: currentPreview.is_draft_source
      ? ("draft" as const)
      : ("active" as const),
    time_created: currentPreview.source_version.time_created || "",
    time_deployed: currentPreview.source_version.time_deployed || null,
    created_by: null,
    deployed_by: null,
    change_summary: null,
    rubi_org_id: orgId || "",
  };

  const previewVersion = {
    ...currentPreview.preview_version,
    id: currentPreview.preview_version.id || "",
    parent_survey_id: currentPreview.parent_survey_id,
    major_version: currentPreview.preview_version.version_number
      ? parseInt(currentPreview.preview_version.version_number.split(".")[0])
      : null,
    minor_version: currentPreview.preview_version.version_number
      ? parseInt(currentPreview.preview_version.version_number.split(".")[1])
      : null,
    status: "draft" as const,
    time_created: currentPreview.preview_version.time_created || "",
    time_deployed: null,
    created_by: null,
    deployed_by: null,
    change_summary: null,
    rubi_org_id: orgId || "",
  };

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      {/* Header */}
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          p: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={handleCancel} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              Bulk Edit Preview: {currentPreview.survey_name}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            {/* Survey navigation */}
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={() =>
                  setCurrentPreviewIndex((prev) => Math.max(0, prev - 1))
                }
                disabled={currentPreviewIndex === 0}
                size="small"
              >
                <PrevIcon />
              </IconButton>
              <Typography variant="body2">
                Survey {currentPreviewIndex + 1} of {previews.length}
              </Typography>
              <IconButton
                onClick={() =>
                  setCurrentPreviewIndex((prev) =>
                    Math.min(previews.length - 1, prev + 1),
                  )
                }
                disabled={currentPreviewIndex === previews.length - 1}
                size="small"
              >
                <NextIcon />
              </IconButton>
            </Box>

            {/* Version view dropdown */}
            <FormControl size="small" variant="outlined" sx={{ minWidth: 180 }}>
              <Select
                value={selectedVersionView}
                onChange={handleVersionViewChange}
                displayEmpty
              >
                <MenuItem value="fromVersion">
                  Preview{" "}
                  {currentPreview.is_draft_source
                    ? "Current Draft"
                    : `Active v${sourceVersion.major_version}.${sourceVersion.minor_version}`}
                </MenuItem>
                <MenuItem value="toVersion">Preview Modified Draft</MenuItem>
                <MenuItem value="compare">Compare JSON</MenuItem>
              </Select>
            </FormControl>

            {/* Main/Condensed toggle */}
            <ToggleButtonGroup
              color="primary"
              value={isCondensedForm}
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) {
                  setIsCondensedForm(newValue);
                }
              }}
              aria-label="Form Type"
              size="small"
            >
              <ToggleButton
                value={false}
                sx={{
                  minWidth: 90,
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: colors.common.white,
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                  },
                }}
              >
                <Badge
                  color="success"
                  variant="dot"
                  invisible={!hasChanges(currentPreview.configuration_diff)}
                  sx={{
                    "& .MuiBadge-badge": {
                      right: -6,
                      top: 2,
                    },
                  }}
                >
                  Main
                </Badge>
              </ToggleButton>
              <ToggleButton
                value={true}
                sx={{
                  minWidth: 90,
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: colors.common.white,
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                  },
                }}
              >
                <Badge
                  color="success"
                  variant="dot"
                  invisible={!hasChanges(currentPreview.pdf_configuration_diff)}
                  sx={{
                    "& .MuiBadge-badge": {
                      right: -6,
                      top: 2,
                    },
                  }}
                >
                  Condensed
                </Badge>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Action buttons */}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={
                isApplying ? <CircularProgress size={20} /> : <CheckIcon />
              }
              onClick={handleApply}
              disabled={isApplying}
            >
              {isApplying ? "Applying..." : "Apply Bulk Edit"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box flex={1} p={2} overflow="hidden">
        {selectedVersionView === "compare" ? (
          <VersionComparison
            fromVersion={sourceVersion}
            toVersion={previewVersion}
            viewType={isCondensedForm ? "pdf_configuration" : "configuration"}
            configurationDiff={currentPreview.configuration_diff}
            pdfConfigurationDiff={currentPreview.pdf_configuration_diff}
          />
        ) : (
          <Box height="100%" overflow="auto">
            <FormBuilder
              onSave={async () => {}}
              mode="view"
              surveyJSObject={
                isCondensedForm
                  ? selectedVersionView === "fromVersion"
                    ? sourceVersion.pdf_configuration
                    : previewVersion.pdf_configuration
                  : selectedVersionView === "fromVersion"
                    ? sourceVersion.configuration
                    : previewVersion.configuration
              }
              isCondensedForm={isCondensedForm}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
